/**
 * «Cómo va el mes» and «Por día del mes» (the owner, 2026-10-13, of what «Análisis» could add:
 * «let's build all of it»): where this month will close at the pace the shop goes, against last
 * month and the same month a year before; how far along the month's goals are; and which days of
 * the month sell the most, the paydays among them.
 *
 * Sales are counted as everywhere in «Análisis» (`netoDeVenta`): what came back off, delivery and
 * cash rounding aside, voided sales nowhere. Pure, so the screen and its tests share one answer.
 */

import { diasEntre, sumarDias, type Centavos } from '@abastece/shared';
import { netoDeVenta, type Venta } from '@abastece/core';
import { otroMes } from './historial.js';

/** The weeks a weekday's usual sales are measured over: two months, four paydays in them. */
export const SEMANAS_DE_RITMO = 8;
/** The days «Por día del mes» averages over: half a year, each day of the month six times. */
export const DIAS_DEL_MES_ATRAS = 182;

const diaSemana = (fecha: string): number => new Date(`${fecha}T00:00:00Z`).getUTCDay();
const finDeMes = (mes: string): string => sumarDias(`${otroMes(mes, 1)}-01`, -1);

interface Suma {
  ingreso: number;
  costo: number;
}

/** Each day's sales, as «Análisis» counts them. */
export function ventasPorDia(ventas: readonly Venta[]): Map<string, Suma> {
  const dias = new Map<string, Suma>();
  for (const v of ventas) {
    if (v.anulada) continue;
    const neto = netoDeVenta(v);
    const d = dias.get(v.fecha) ?? { ingreso: 0, costo: 0 };
    d.ingreso += neto.ingreso;
    d.costo += neto.costo;
    dias.set(v.fecha, d);
  }
  return dias;
}

const sumarEntre = (dias: ReadonlyMap<string, Suma>, desde: string, hasta: string): Suma => {
  const s = { ingreso: 0, costo: 0 };
  for (const [d, x] of dias) {
    if (d < desde || d > hasta) continue;
    s.ingreso += x.ingreso;
    s.costo += x.costo;
  }
  return s;
};

/**
 * Each weekday's sales the last eight weeks before today, Sunday first, and how many of each
 * weekday that was: from the shop's first sale on, days that sold nothing counted as nothing. And
 * what those weeks sold and cost together.
 */
function semanasDeRitmo(porDia: ReadonlyMap<string, Suma>, hoyEs: string): { suma: number[]; cuenta: number[]; ritmo: Suma } {
  const primeraVenta = [...porDia.keys()].sort()[0] ?? null;
  const desdeRitmo = sumarDias(hoyEs, -7 * SEMANAS_DE_RITMO);
  const inicio = primeraVenta !== null && primeraVenta > desdeRitmo ? primeraVenta : desdeRitmo;
  const suma = Array<number>(7).fill(0);
  const cuenta = Array<number>(7).fill(0);
  const ritmo = { ingreso: 0, costo: 0 };
  if (primeraVenta !== null) {
    for (let d = inicio; d < hoyEs; d = sumarDias(d, 1)) {
      const s = porDia.get(d);
      suma[diaSemana(d)]! += s?.ingreso ?? 0;
      cuenta[diaSemana(d)]! += 1;
      ritmo.ingreso += s?.ingreso ?? 0;
      ritmo.costo += s?.costo ?? 0;
    }
  }
  return { suma, cuenta, ritmo };
}

/**
 * What a day of each weekday usually sells, Sunday first: its average the last eight weeks, as
 * «Cómo va el mes» expects it. With fewer than two of every weekday behind it, every day at what a
 * day has sold since the first sale, today in (a week at least); nothing sold, nothing.
 */
export function ventaUsualPorDiaSemana(ventas: readonly Venta[], hoyEs: string): number[] {
  const porDia = ventasPorDia(ventas);
  const { suma, cuenta } = semanasDeRitmo(porDia, hoyEs);
  if (cuenta.every((n) => n >= 2)) return suma.map((x, i) => x / cuenta[i]!);
  const primeraVenta = [...porDia.keys()].sort()[0] ?? null;
  if (primeraVenta === null) return Array<number>(7).fill(0);
  const desdeRitmo = sumarDias(hoyEs, -7 * SEMANAS_DE_RITMO);
  const desde = primeraVenta > desdeRitmo ? primeraVenta : desdeRitmo;
  const vendido = sumarEntre(porDia, desde, hoyEs).ingreso;
  return Array<number>(7).fill(vendido / Math.max(7, diasEntre(desde, hoyEs) + 1));
}

/** What a whole month sold and left; null when it sold nothing. */
export interface Comparado {
  ingreso: Centavos;
  utilidad: Centavos;
}

export interface ProyeccionDelMes {
  /** `YYYY-MM`. */
  mes: string;
  /** Days the month has, and how many have gone by, today counted in. */
  dias: number;
  transcurridos: number;
  vendido: Centavos;
  utilidad: Centavos;
  proyectado: Centavos;
  utilidadProyectada: Centavos;
  /**
   * How the rest of the month was guessed: `semana`, what each weekday has been selling the last
   * eight weeks; `ritmo`, with fewer than two of each weekday behind it, this month's own pace,
   * from the shop's first sale when that came this month.
   */
  segun: 'semana' | 'ritmo';
  mesPasado: Comparado | null;
  haceUnAnio: Comparado | null;
}

/**
 * Where this month will close: what it sold so far, today's rest at what a day like today usually
 * sells, and every day left at what its weekday has been selling the last eight weeks — days with
 * no sales counted as the nothing they sold, from the shop's first sale on. Profit at the margin of
 * those same weeks. With less than two of every weekday behind it, the month's own pace instead:
 * from the shop's first sale if it came this month, the days before it not counted as selling
 * nothing, since the shop was not keeping them here.
 */
export function proyeccionDelMes(ventas: readonly Venta[], hoyEs: string): ProyeccionDelMes {
  const mes = hoyEs.slice(0, 7);
  const primero = `${mes}-01`;
  const ultimo = finDeMes(mes);
  const porDia = ventasPorDia(ventas);
  const ahora = sumarEntre(porDia, primero, hoyEs);
  const transcurridos = diasEntre(primero, hoyEs) + 1;
  const dias = diasEntre(primero, ultimo) + 1;

  const primeraVenta = [...porDia.keys()].sort()[0] ?? null;
  const { suma, cuenta, ritmo } = semanasDeRitmo(porDia, hoyEs);
  const segun: ProyeccionDelMes['segun'] = cuenta.every((n) => n >= 2) ? 'semana' : 'ritmo';

  let resto = 0;
  let margen: number;
  if (segun === 'semana') {
    const usual = (d: string) => suma[diaSemana(d)]! / cuenta[diaSemana(d)]!;
    resto += Math.max(0, usual(hoyEs) - (porDia.get(hoyEs)?.ingreso ?? 0));
    for (let d = sumarDias(hoyEs, 1); d <= ultimo; d = sumarDias(d, 1)) resto += usual(d);
    margen = ritmo.ingreso > 0 ? (ritmo.ingreso - ritmo.costo) / ritmo.ingreso : 0;
  } else {
    const desde = primeraVenta !== null && primeraVenta > primero ? primeraVenta : primero;
    resto = (ahora.ingreso / Math.max(1, diasEntre(desde, hoyEs) + 1)) * (dias - transcurridos);
    margen = ahora.ingreso > 0 ? (ahora.ingreso - ahora.costo) / ahora.ingreso : 0;
  }

  const comparado = (desde: string, hasta: string): Comparado | null => {
    const s = sumarEntre(porDia, desde, hasta);
    return s.ingreso === 0 && s.costo === 0 ? null : { ingreso: s.ingreso as Centavos, utilidad: (s.ingreso - s.costo) as Centavos };
  };
  const pasado = otroMes(mes, -1);
  const anioAntes = `${Number(mes.slice(0, 4)) - 1}${mes.slice(4)}`;
  return {
    mes,
    dias,
    transcurridos,
    vendido: ahora.ingreso as Centavos,
    utilidad: (ahora.ingreso - ahora.costo) as Centavos,
    proyectado: Math.round(ahora.ingreso + resto) as Centavos,
    utilidadProyectada: Math.round(ahora.ingreso - ahora.costo + resto * margen) as Centavos,
    segun,
    mesPasado: comparado(`${pasado}-01`, finDeMes(pasado)),
    haceUnAnio: comparado(`${anioAntes}-01`, finDeMes(anioAntes)),
  };
}

/** A goal for the month and how far along it is. */
export interface AvanceMeta {
  meta: Centavos;
  llevado: Centavos;
  /** What is done of it, in basis points: past 10,000 once it is passed. */
  parteBp: number;
  /** Whether the month gets there at the pace it goes. */
  llega: boolean;
  /** What each day left, today counted in, would have to bring for it; null once it is reached. */
  faltaAlDia: Centavos | null;
}

export function avanceDeMeta(meta: number, llevado: number, proyectado: number, quedan: number): AvanceMeta {
  return {
    meta: meta as Centavos,
    llevado: llevado as Centavos,
    parteBp: meta > 0 ? Math.round((llevado * 10_000) / meta) : 0,
    llega: proyectado >= meta,
    faltaAlDia: llevado >= meta ? null : (Math.ceil((meta - llevado) / Math.max(1, quedan)) as Centavos),
  };
}

/** One day of the month and what it usually sells. */
export interface DiaDelMes {
  /** 1 to 31. */
  dia: number;
  /** What a month's day of this number sold, on average. */
  promedio: Centavos;
  /** How many of them are in the average: a month without a 31st has none to give. */
  fechas: number;
}

/**
 * What each day of the month sells, on average over `desde` to `hasta` (the shop's days, from its
 * first sale on): the paydays on the 15th and at the month's end show as their own peaks. Every
 * date counts, those that sold nothing too.
 */
export function porDiaDelMes(ventas: readonly Venta[], desde: string, hasta: string): DiaDelMes[] {
  const porDia = ventasPorDia(ventas);
  const primeraVenta = [...porDia.keys()].sort()[0];
  const suma = Array<number>(32).fill(0);
  const fechas = Array<number>(32).fill(0);
  if (primeraVenta !== undefined) {
    for (let d = primeraVenta > desde ? primeraVenta : desde; d <= hasta; d = sumarDias(d, 1)) {
      const n = Number(d.slice(8));
      suma[n]! += porDia.get(d)?.ingreso ?? 0;
      fechas[n]! += 1;
    }
  }
  return Array.from({ length: 31 }, (_, i) => ({
    dia: i + 1,
    promedio: (fechas[i + 1]! > 0 ? Math.round(suma[i + 1]! / fechas[i + 1]!) : 0) as Centavos,
    fechas: fechas[i + 1]!,
  }));
}

/**
 * The days of the month that sell the most, best first, and how much more than an average day
 * the best one sells, in basis points. Nothing while no day sold anything.
 */
export function diasFuertes(dias: readonly DiaDelMes[], cuantos = 3): { dias: number[]; sobrePromedioBp: number } | null {
  const conVentas = dias.filter((d) => d.promedio > 0);
  if (conVentas.length === 0) return null;
  const total = dias.reduce((a, d) => a + d.promedio * d.fechas, 0);
  const fechas = dias.reduce((a, d) => a + d.fechas, 0);
  const promedio = fechas > 0 ? total / fechas : 0;
  const mejores = [...conVentas].sort((a, b) => b.promedio - a.promedio || a.dia - b.dia).slice(0, cuantos);
  return {
    dias: mejores.map((d) => d.dia),
    sobrePromedioBp: promedio > 0 ? Math.round(((mejores[0]!.promedio - promedio) * 10_000) / promedio) : 0,
  };
}
